import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import { relatedComponentNameForDef } from "%/dynamic-component-editor/helpers/host-bound-component";

class BaseDropSelection {
  static optionKey = "";
  static modalTitle = "Select";
  static promptText = "";
  static rowLabel = "Selection";
  static placeholder = "Select an option";
  static createItemLabel = "";
  static defaultNewItemName = "New item";
  static createPlaceholder = "Name your new item";
  static pickExistingLabel = "Choose an existing item instead";

  static #selectionsByElementType = new Map();

  static #componentLinkedFallback = null;

  static register(elementType, selectionClass) {
    if (!elementType || typeof elementType !== "string") {
      throw new Error("BaseDropSelection.register needs an element type");
    }
    const rows = selectionClass?.rows?.() ?? [];
    if (!rows.length || rows.some(row => !row.optionKey)) {
      throw new Error(`${elementType} drop selection must declare an optionKey for every row`);
    }
    BaseDropSelection.#selectionsByElementType.set(elementType, selectionClass);
  }

  static forElementType(elementType) {
    return BaseDropSelection.#selectionsByElementType.get(elementType) ?? null;
  }

  // XXX: Registered by LinkedComponentSelection itself. A direct import here would
  // XXX: be a cycle, since that class extends this one.
  static registerComponentLinkedFallback(selectionClass) {
    BaseDropSelection.#componentLinkedFallback = selectionClass;
  }

  // XXX: forElementType is a static Map, so it only ever knew the embed and the
  // XXX: ResumeCard. Every element that renders another component must raise the
  // XXX: picker on drop, and an element declares that about itself through
  // XXX: relatedComponents -- so the fallback is derived, never a hand-kept list
  // XXX: that drifts out of step with the elements.
  static forDrop(elementType, elementDef = null) {
    const registered = BaseDropSelection.forElementType(elementType);
    if (registered) {
      return registered;
    }
    if (!relatedComponentNameForDef(elementDef, elementType)) {
      return null;
    }
    return BaseDropSelection.#componentLinkedFallback;
  }

  static registeredElementTypes() {
    return [...BaseDropSelection.#selectionsByElementType.keys()];
  }

  static rows() {
    return [{
      optionKey: this.optionKey,
      rowLabel: this.rowLabel,
      placeholder: this.placeholder,
      optional: false,
    }];
  }

  static valueFromOptions(optionsTree, optionKey = null) {
    return DynamicElement.defaultOptionValue(optionsTree, optionKey ?? this.optionKey);
  }

  static isEmptyValue(value) {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    return Number(value) === 0;
  }

  static coerceValue(value) {
    return Number(value) || 0;
  }

  static needsSelectionOnDrop(optionsTree) {
    return this.rows().some(
      row => !row.optional && this.isEmptyValue(this.valueFromOptions(optionsTree, row.optionKey)),
    );
  }

  static canCreate() {
    return Boolean(this.createItemLabel);
  }

  static candidates() {
    return [];
  }

  static suggestedValues() {
    return {};
  }

  static createCandidate() {
    throw new Error(`${this.name} declared createItemLabel without implementing createCandidate`);
  }
}

export default BaseDropSelection;
